import { useState } from 'react'
import { Barbell, Bell } from '@phosphor-icons/react'
import {
  checkNotificationPermission,
  openNotificationSettings,
  requestNotificationPermission,
} from '@/core/notifications'

interface NotificationPermissionScreenProps {
  onComplete: () => void
  onSkip: () => void
}

export function NotificationPermissionScreen({
  onComplete,
  onSkip,
}: NotificationPermissionScreenProps) {
  const [requesting, setRequesting] = useState(false)

  const handleEnable = async () => {
    setRequesting(true)

    try {
      const currentPermission = await checkNotificationPermission()

      if (currentPermission === 'granted') {
        onComplete()
        return
      }

      const nextPermission =
        currentPermission === 'denied'
          ? currentPermission
          : await requestNotificationPermission()

      if (nextPermission === 'denied') {
        await openNotificationSettings()
      }

      onComplete()
    } catch (error) {
      console.error('Failed to enable notifications:', error)
      onComplete()
    } finally {
      setRequesting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
          <Bell size={32} weight="bold" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Stay on Track
        </h2>
        <p className="text-gray-600 dark:text-slate-400">
          Get reminders when your workout is starting
        </p>
      </div>

      {/* Notification Preview */}
      <div className="mx-auto max-w-sm rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-slate-800/50 p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between text-xs uppercase tracking-wide text-gray-500 dark:text-slate-500">
          <span>Notification Preview</span>
          <span>now</span>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
              <Barbell size={20} weight="bold" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Start Grounded</p>
              <p className="text-xs text-gray-500 dark:text-slate-500">Workout reminder</p>
            </div>
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">Time to train!</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
            Your strength workout is starting now
          </p>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-3 pt-4">
        <button
          type="button"
          data-testid="onboarding-enable-notifications-button"
          onClick={handleEnable}
          disabled={requesting}
          className="w-full px-4 py-3 rounded-lg font-medium bg-primary-600 hover:bg-primary-700 text-white shadow-lg shadow-primary-600/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {requesting ? 'Enabling...' : 'Enable Notifications'}
        </button>
        <button
          type="button"
          data-testid="onboarding-skip-notifications-button"
          onClick={onSkip}
          disabled={requesting}
          className="w-full px-4 py-3 rounded-lg font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
        >
          Skip for now
        </button>
      </div>
    </div>
  )
}
